import { Field as FieldHouse, Form as FormHouse } from 'houseform'
import { Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { PATHS } from '@/app/router/routes'
import type { IUserResetPassword } from '@/features/users/models/user'
import Loading from '@/shared/components/loading'
import { ERROR_MESSAGES } from '@/shared/constants/errors/messages'
import { HttpStatus } from '@/shared/constants/http-status'
import { FormField } from '@/shared/form/form-field'
import { FormInput } from '@/shared/form/form-input'
import { FormLabel } from '@/shared/form/form-label'
import { FormMessages } from '@/shared/form/form-messages'
import { Button } from '@/shared/ui/button'
import { TypographyParagraph } from '@/shared/ui/typography/paragraph'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { AUTH_RESET_PASSWORD_SCHEMA, AUTH_SCHEMA_VALIDATION } from '../auth.schema'
import { resetPassword, verifyTokenResetPassword } from '../auth.service'
import LayoutAuth from '../components/layout-auth'
import useAuth from '../hooks/use-auth'

const ResetPassword = () => {
  const { id, token } = useParams()
  const navigate = useNavigate()

  const { auth } = useAuth()
  const [loadingVerify, setLoadingVerify] = useState(true)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (values: IUserResetPassword) => {
    if (!id || !token) return
    try {
      setLoading(true)
      const response = await resetPassword(id, token, values)
      if (response.status === HttpStatus.OK && response.data?.success) {
        toast.success('¡Se ha cambiado tu contraseña!')
        navigate(PATHS.auth.login, { replace: true })
      } else {
        // Cualquier no-éxito (no sólo 401): mensaje claro. Traducción del código si existe;
        // nunca un toast vacío por indexar una key inexistente en ERROR_MESSAGES.
        toast.error(ERROR_MESSAGES[response.data?.message] ?? ERROR_MESSAGES.GENERIC_ERROR)
      }
    } catch (e) {
      console.error(e)
      toast.error(getErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const verify = async () => {
      // Link de reset malformado (sin id o token en la URL) = inválido
      if (!id || !token) {
        setLoadingVerify(false)
        setError(true)
        return
      }
      try {
        const response = await verifyTokenResetPassword(id, token)
        if (response.status === HttpStatus.OK && response.data?.id) {
          setLoadingVerify(false)
        } else if (response.status >= HttpStatus.UNAUTHORIZED) {
          setLoadingVerify(false)
          setError(true)
        }
      } catch (e) {
        console.error(e)
        setLoadingVerify(false)
        setError(true)
      }
    }
    verify()
  }, [])

  if (auth?.id) {
    navigate(PATHS.admin.root, { replace: true })
  }
  if (loadingVerify)
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loading />
      </div>
    )

  if (error) {
    return (
      <LayoutAuth title="Cambio de contraseña">
        <TypographyParagraph className="text-center">
          ¡El token ha expirado o no es válido!
          <br />
          Vuelve a solicitar el cambio de contraseña
        </TypographyParagraph>
        <Button variant={'link'} asChild>
          <Link to={PATHS.auth.forgotPassword}>Solicitar cambio de contraseña</Link>
        </Button>
      </LayoutAuth>
    )
  }

  return (
    <LayoutAuth title="Cambio de contraseña">
      <FormHouse onSubmit={handleSubmit}>
        {({ submit }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
            className="grid grid-cols-1 w-full gap-6"
          >
            <FieldHouse<string>
              name={AUTH_RESET_PASSWORD_SCHEMA.PASSWORD}
              onBlurValidate={AUTH_SCHEMA_VALIDATION.password}
              onSubmitTransform={(value) => value.trim()}
            >
              {({ value, setValue, errors, onBlur }) => (
                <FormField required messages={errors.map((e) => e)}>
                  <FormLabel>Contraseña</FormLabel>
                  <FormInput
                    className="lowercase"
                    type="password"
                    autoComplete="new-password"
                    value={value}
                    onChange={(e) => {
                      setValue(e.target.value)
                    }}
                    onBlur={onBlur}
                  />
                  <FormMessages />
                </FormField>
              )}
            </FieldHouse>
            <FieldHouse<string>
              name={AUTH_RESET_PASSWORD_SCHEMA.CONFIRM_PASSWORD}
              listenTo={[AUTH_RESET_PASSWORD_SCHEMA.PASSWORD]}
              onSubmitTransform={(value) => value.trim()}
              onBlurValidate={async (val, form) => {
                if (val === form.getFieldValue(AUTH_RESET_PASSWORD_SCHEMA.PASSWORD)?.value) {
                  return true
                } else {
                  throw 'Las contraseñas no coinciden'
                }
              }}
            >
              {({ value, setValue, errors, onBlur }) => (
                <FormField required messages={errors.map((e) => e)}>
                  <FormLabel>Confirmación de contraseña</FormLabel>
                  <FormInput
                    className="lowercase"
                    type="password"
                    autoComplete="new-password"
                    value={value}
                    onChange={(e) => {
                      setValue(e.target.value)
                    }}
                    onBlur={onBlur}
                  />
                  <FormMessages />
                </FormField>
              )}
            </FieldHouse>
            <Button type="submit">
              {loading ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Cambiando contraseña...
                </>
              ) : (
                'Cambiar mi contraseña'
              )}
            </Button>
          </form>
        )}
      </FormHouse>
    </LayoutAuth>
  )
}

export default ResetPassword
